import { wsUrl } from "#staxmpc/lib/config";
import { MPCClient, randomSeed, SignAlgo } from "#staxmpc/lib/dkls";
import { decodeHex, encodeHex } from "#staxmpc/lib/encode";
import { MsgRelayClient } from "#staxmpc/lib/relay";
import { MPCShare } from "#staxmpc/stores/mpc";
import { getPublicKey, schnorr_join_dkg, schnorr_join_dsg, SchnorrKeyshare, verifyingKey } from "@stavax/schnorr-wasm";
export class EdDSAKeyShare {
  mpcShare;
  config;
  mpcClient;
  algo = SignAlgo.EdDSA;
  constructor(share, config) {
    this.mpcShare = share;
    this.config = config;
    this.mpcClient = new MPCClient(config);
  }
  get publicKey() {
    return this.mpcShare.share.publicKey();
  }
  get keyId() {
    return encodeHex(this.mpcShare.share.keyId());
  }
  async signMessage(hashedMessage) {
    const hexMessage = hashedMessage.startsWith("0x") ? hashedMessage.slice(2) : hashedMessage;
    const resp = await this.runDSG(
      this.mpcShare,
      { hash: decodeHex(hexMessage) }
    );
    return "0x" + encodeHex(resp[0]);
  }
  async runDSG(mpcShare, message) {
    const { setup, instance } = this.mpcClient.createSignSetup(
      [mpcShare.verifyingKey, this.config.server.verifyingKey],
      mpcShare.share.keyId(),
      message,
      this.config.requestTTL
    );
    const relayURL = wsUrl(this.config.setup.relay);
    const abort = new AbortController();
    const ws = await MsgRelayClient.connect(relayURL, abort.signal);
    try {
      ws.send(setup);
      const schnorrKeyshare = SchnorrKeyshare.fromBytes(mpcShare.share.toBytes());
      return await Promise.all([
        schnorr_join_dsg(
          instance,
          this.config.setup.verifyingKey,
          mpcShare.secretKey,
          relayURL,
          randomSeed(32),
          async () => schnorrKeyshare
        ),
        this.mpcClient.startDsg(SignAlgo.EdDSA, instance, mpcShare.share.keyId(), setup)
      ]);
    } finally {
      await ws.close();
    }
  }
  static async generateWallet(config) {
    const threshold = 2;
    const mpcClient = new MPCClient(config);
    const clients = [0, 1].map(() => {
      const secret = randomSeed(32);
      const decryptKey = randomSeed(32);
      return {
        verifyingKey: verifyingKey(secret),
        secretKey: secret,
        encryptionKey: getPublicKey(decryptKey),
        decryptionKey: decryptKey
      };
    });
    const msgRelayUrl = wsUrl(config.setup.relay);
    const { setup, instance } = mpcClient.createKeygenSetup(
      SignAlgo.EdDSA,
      clients,
      threshold,
      config.requestTTL
    );
    const abort = new AbortController();
    const ws = await MsgRelayClient.connect(msgRelayUrl, abort.signal);
    try {
      ws.send(setup);
      const [keyShare1, keyShare2, _] = await Promise.all([
        ...clients.map((c) => schnorr_join_dkg(
          instance,
          config.setup.verifyingKey,
          c.secretKey,
          c.decryptionKey,
          msgRelayUrl,
          randomSeed(32),
          async (_2) => true
        )),
        mpcClient.startDkg(SignAlgo.EdDSA, instance, setup)
      ]);
      return [
        new MPCShare(SignAlgo.EdDSA, clients[0].secretKey, keyShare1, clients[0].decryptionKey),
        new MPCShare(SignAlgo.EdDSA, clients[1].secretKey, keyShare2, clients[1].decryptionKey)
      ];
    } finally {
      await ws.close();
    }
  }
}
